/**
 * Host bridge: Electron preload (`window.api`) vs Tauri (`invoke`) vs browser fallbacks.
 * Prefer these helpers over scattered `window.electron` / `window.api` checks.
 */

import { isInTauriShell } from './detector'
import type { SubscriptionStateRow } from './types'

/** Low-level Tauri invoke. Dynamic import avoids bundling `@tauri-apps/api` in Electron builds. */
export async function invokeTauri<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke } = await import('@tauri-apps/api/core')
  return invoke<T>(command, args)
}

export function hasElectronPreload(): boolean {
  return typeof window !== 'undefined' && typeof window.electron !== 'undefined'
}

/** True for Electron or Tauri — native shell (distinct from pure browser tab). */
export function isNativeShell(): boolean {
  if (typeof window === 'undefined') return false
  return hasElectronPreload() || isInTauriShell()
}

/** Electron IPC surface when `window.electron` is set (undefined in browser/Tauri-only web). */
export function getPreloadApi(): Window['api'] | undefined {
  if (!hasElectronPreload()) return undefined
  return window.api
}

export async function getInternetStatus(): Promise<boolean> {
  const api = getPreloadApi()
  if (api?.getInternetStatus) return api.getInternetStatus()
  return typeof navigator !== 'undefined' && navigator.onLine
}

/** Subscribe to online/offline; uses Electron IPC when available, else browser events. */
export function onInternetStatusChanged(callback: (online: boolean) => void): () => void {
  const api = getPreloadApi()
  if (typeof api?.onInternetStatusChanged === 'function') {
    return api.onInternetStatusChanged(callback)
  }
  const on = (): void => {
    callback(typeof navigator !== 'undefined' && navigator.onLine)
  }
  window.addEventListener('online', on)
  window.addEventListener('offline', on)
  return (): void => {
    window.removeEventListener('online', on)
    window.removeEventListener('offline', on)
  }
}

export async function getHostAppVersion(): Promise<string | null> {
  const api = getPreloadApi()
  if (typeof api?.getAppVersion === 'function') {
    try {
      return await api.getAppVersion()
    } catch {
      return null
    }
  }
  if (isInTauriShell()) {
    try {
      const info = await invokeTauri<{ appVersion: string }>('get_runtime_info')
      return info.appVersion ?? null
    } catch {
      return null
    }
  }
  return null
}

export async function getTunnelStatus(): Promise<{ url: string | null; isActive: boolean }> {
  const api = getPreloadApi()
  if (typeof api?.getTunnelStatus === 'function') {
    try {
      return await api.getTunnelStatus()
    } catch {
      return { url: null, isActive: false }
    }
  }
  return { url: null, isActive: false }
}

export async function setIdleState(isIdle: boolean): Promise<void> {
  const api = getPreloadApi()
  if (typeof api?.setIdleState === 'function') {
    await api.setIdleState(isIdle).catch(() => undefined)
  }
}

export async function getSubscriptionState(): Promise<SubscriptionStateRow | null> {
  const api = getPreloadApi()
  if (typeof api?.getSubscriptionState === 'function') {
    try {
      return await api.getSubscriptionState()
    } catch {
      return null
    }
  }
  return null
}

export async function saveSubscriptionState(row: Partial<SubscriptionStateRow>): Promise<void> {
  const api = getPreloadApi()
  if (typeof api?.saveSubscriptionState === 'function') {
    await api.saveSubscriptionState(row)
  }
}

export async function clearSubscriptionState(): Promise<void> {
  const api = getPreloadApi()
  if (typeof api?.clearSubscriptionState === 'function') {
    await api.clearSubscriptionState()
  }
}

/**
 * Durable device identity (subscription binding). Returns the id to use, or
 * null when the shell doesn't support it yet (old packaged versions) — the
 * caller keeps the localStorage value in that case.
 */
export async function resolveStableDeviceId(payload: {
  candidate?: string
  rendererHadStore?: boolean
}): Promise<string | null> {
  const api = getPreloadApi()
  if (typeof api?.resolveStableDeviceId === 'function') {
    try {
      return await api.resolveStableDeviceId(payload)
    } catch {
      return null
    }
  }
  return null
}

export async function syncAuthTokens(): Promise<void> {
  const api = getPreloadApi()
  if (typeof api?.syncAuthTokens === 'function') {
    await api.syncAuthTokens()
  }
}

export async function clearAuthTokens(): Promise<void> {
  const api = getPreloadApi()
  if (typeof api?.clearAuthTokens === 'function') {
    await api.clearAuthTokens()
  }
}

export async function startGoogleOAuth(
  mode: 'login' | 'register' = 'login'
): Promise<{ success: boolean; error?: string; token?: string; user?: { email: string; name: string } }> {
  const api = getPreloadApi()
  if (!api || typeof api.startGoogleOAuth !== 'function') {
    throw new Error('Google OAuth is only available in the desktop app with OAuth support')
  }
  return api.startGoogleOAuth(mode)
}

export function hasReprintSessionApi(): boolean {
  const api = getPreloadApi()
  return !!(
    api?.reprint &&
    typeof api.reprint.listSessions === 'function' &&
    typeof api.reprint.getSessionPhotos === 'function'
  )
}

/**
 * The disk cleanup IPC exists only on shells built with it. The renderer is
 * web-served, so it reaches older shells too; there the UI stays hidden.
 */
export function getDiskCleanupApi(): NonNullable<Window['api']['diskCleanup']> | null {
  const api = getPreloadApi()?.diskCleanup
  return api &&
    typeof api.getStatus === 'function' &&
    typeof api.plan === 'function' &&
    typeof api.run === 'function'
    ? api
    : null
}

// ───────────────────────── Payment popup (toyyibPay) ──────────────────────────
//
// The redirect-based payment flow (toyyibPay today) opens the gateway's bill
// page in a child BrowserWindow. The IPC surface is only available in the
// Electron shell; in Tauri/browser builds these helpers no-op gracefully so
// callers in the renderer never need to do their own shell detection.

export type ToyyibpayBillResult = {
  status: 'completed' | 'closed' | 'failed' | 'reused' | 'unavailable'
  reachedReturnUrl?: boolean
  finalUrl?: string
  reason?: string
}

export function hasToyyibpayBillApi(): boolean {
  const api = getPreloadApi()
  return typeof api?.openToyyibpayBill === 'function'
}

export async function openToyyibpayBill(
  url: string,
  returnUrlPattern?: string
): Promise<ToyyibpayBillResult> {
  const api = getPreloadApi()
  if (typeof api?.openToyyibpayBill !== 'function') {
    return { status: 'unavailable', reason: 'no-electron-preload' }
  }
  try {
    return await api.openToyyibpayBill(url, returnUrlPattern)
  } catch (error) {
    return {
      status: 'failed',
      reason: error instanceof Error ? error.message : 'unknown'
    }
  }
}

export async function closeToyyibpayBill(): Promise<void> {
  const api = getPreloadApi()
  if (typeof api?.closeToyyibpayBill !== 'function') return
  try {
    await api.closeToyyibpayBill()
  } catch {
    // No-op: best-effort close, e.g. on page-unload cleanup.
  }
}

export function onToyyibpayReturn(
  callback: (info: { url: string }) => void
): () => void {
  const api = getPreloadApi()
  if (typeof api?.onToyyibpayReturn !== 'function') {
    return () => {
      /* no-op outside Electron */
    }
  }
  return api.onToyyibpayReturn(callback)
}
